// FAZ-10 / F2 / K-26 — componentJson ağacının STATİK React render'ı (Puck instance
// YOK). Sayfa-yapıcıda bir cmp_ bloğu, tanımının componentJson'ıyla bu bileşen
// üzerinden canlandırılır. Studio edit-canvas ile AYNI View bileşenlerini
// (blocks/blockViews) kullanır → editör/önizleme/sayfa yapısal olarak aynı çıktı.
//
// Dış prop binding (K-22): string alanlardaki `{{key}}` placeholder'ları sayfadaki
// cmp_ blok props değerleriyle değiştirilir. GÜVENLİK:
//   • html alanları (text.html, legacyHtml.html): enjekte edilen değer substitüsyondan
//     ÖNCE HTML-escape edilir (attribute/element bağlamı güvenli), sonra alan bütünü
//     View içinde sanitizeHtml'den geçer. Tek geçiş → escape edilmiş değer yeni
//     placeholder üretemez (re-enjeksiyon yok).
//   • düz alanlar: ham substitüsyon; React render'da metni escape eder. URL alanları
//     ayrıca View'ın isSafeUrl geçidinden geçer.
//   • boolean/number kullanıcı-görünür basılır (ham `false` literal bug'ı geri gelmez);
//     tanımsız/eksik/nesne/dizi prop → boş string.

import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { escapeHtml, PLACEHOLDER_REGEX } from '../utils/template';
import { parseDefaultStyle } from './defaultStyle';
import {
  clampColumnCount,
  clampMapZoom,
  normalizeHeadingLevel,
  type AlertTone,
  type BadgeTone,
  type BlockAlign,
  type ButtonSize,
  type ButtonVariant,
  type ContainerAlign,
  type ContainerBackground,
  type ContainerPadding,
  type GridGap,
  type HeadingTone,
  type IconName,
  type IconTone,
  type ImageRounded,
  type ImageWidth,
  type SocialLinkItem,
  type SocialSize,
  type VideoSource,
} from './studioBlocks';
import {
  ButtonView,
  ContainerView,
  GridView,
  HeadingView,
  ImageView,
  LegacyHtmlView,
  SpacerView,
  TextView,
} from './blockViews';
import {
  AccordionView,
  AlertView,
  BadgeView,
  DividerView,
  FormRefView,
  HtmlEmbedView,
  IconView,
  ListView,
  MapRefView,
  QuoteView,
  SliderRefView,
  SocialLinksView,
  StatView,
  VideoView,
} from './blockViewsExtended';

// FAZ-10.6 review LOW-3 — BE `ComponentConstraints.MaxComponentJsonDepth` (8) ile
// birebir parite: BE store-time'da bu derinliği zaten reddeder, dolayısıyla geçerli
// (store'lanmış) bir ağaç hiçbir zaman bu cap'e takılmaz; bozuk/harici JSON'da
// özyineyi güvenle durdurur.
/** BE derinlik cap'i (8) aynası — bozuk/derin ağaçta özyineyi güvenle durdurur. */
const MAX_DEPTH = 8;

interface JsonNode {
  type: string;
  props: Record<string, unknown>;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/** K-29 — opsiyonel string token/styleJson; string değilse undefined (View default'una düşer). */
function strU(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Bir prop değerinin kullanıcı-görünür string basımı; nesne/dizi/tanımsız → boş. */
function printValue(raw: unknown): string {
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);
  return '';
}

/** Düz alan: ham substitüsyon (React render-zamanı escape eder). */
function bindPlain(text: string, values: Record<string, unknown>): string {
  return text.replace(PLACEHOLDER_REGEX, (_m, key: string) => printValue(values[key]));
}

/** HTML alanı: enjekte edilen değer önce HTML-escape; alan bütünü View'da sanitize. */
function bindHtml(html: string, values: Record<string, unknown>): string {
  return html.replace(PLACEHOLDER_REGEX, (_m, key: string) => escapeHtml(printValue(values[key])));
}

/** Bir prop değerini string dizisine süzer (list.items). */
function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string');
}

/** Bir prop değerini sosyal bağlantı dizisine süzer. */
function asSocialLinks(value: unknown): SocialLinkItem[] {
  if (!Array.isArray(value)) return [];
  const out: SocialLinkItem[] = [];
  for (const el of value) {
    if (!el || typeof el !== 'object') continue;
    const platform = (el as { platform?: unknown }).platform;
    const url = (el as { url?: unknown }).url;
    if (typeof platform === 'string' && typeof url === 'string') {
      out.push({ platform: platform as SocialLinkItem['platform'], url });
    }
  }
  return out;
}

/** Bir prop değerini slot dizisine (`{type,props}[]`) süzer; değilse boş dizi. */
function asNodes(value: unknown): JsonNode[] {
  if (!Array.isArray(value)) return [];
  const out: JsonNode[] = [];
  for (const el of value) {
    if (!el || typeof el !== 'object' || Array.isArray(el)) continue;
    const type = (el as { type?: unknown }).type;
    if (typeof type !== 'string') continue;
    const props = (el as { props?: unknown }).props;
    out.push({
      type,
      props: props && typeof props === 'object' ? (props as Record<string, unknown>) : {},
    });
  }
  return out;
}

function renderNode(
  node: JsonNode,
  values: Record<string, unknown>,
  depth: number,
  key: number,
): ReactElement | null {
  if (depth > MAX_DEPTH) return null;
  const p = node.props ?? {};
  switch (node.type) {
    case 'heading':
      return (
        <HeadingView
          key={key}
          text={bindPlain(str(p.text), values)}
          level={normalizeHeadingLevel(p.level)}
          tone={p.tone as HeadingTone | undefined}
          align={p.align as BlockAlign | undefined}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'text':
      return (
        <TextView
          key={key}
          html={bindHtml(str(p.html), values)}
          align={p.align as BlockAlign | undefined}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'image':
      return (
        <ImageView
          key={key}
          src={bindPlain(str(p.src), values)}
          alt={bindPlain(str(p.alt), values)}
          width={p.width as ImageWidth | undefined}
          rounded={p.rounded as ImageRounded | undefined}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'button':
      return (
        <ButtonView
          key={key}
          label={bindPlain(str(p.label), values)}
          href={bindPlain(str(p.href), values)}
          variant={(p.variant as ButtonVariant) ?? 'primary'}
          size={p.size as ButtonSize | undefined}
          fullWidth={p.fullWidth === true}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'container':
      return (
        <ContainerView
          key={key}
          align={(p.align as ContainerAlign) ?? 'left'}
          padding={(p.padding as ContainerPadding) ?? 'medium'}
          background={p.background as ContainerBackground | undefined}
          styleJson={strU(p.styleJson)}
        >
          {renderNodes(asNodes(p.children), values, depth + 1)}
        </ContainerView>
      );
    case 'grid':
      return (
        <GridView
          key={key}
          columnCount={clampColumnCount(p.columnCount)}
          gap={p.gap as GridGap | undefined}
          styleJson={strU(p.styleJson)}
          col1={renderNodes(asNodes(p.col1), values, depth + 1)}
          col2={renderNodes(asNodes(p.col2), values, depth + 1)}
          col3={renderNodes(asNodes(p.col3), values, depth + 1)}
          col4={renderNodes(asNodes(p.col4), values, depth + 1)}
        />
      );
    case 'spacer':
      return <SpacerView key={key} height={Number(p.height)} styleJson={strU(p.styleJson)} />;
    case 'divider':
      return <DividerView key={key} styleJson={strU(p.styleJson)} />;
    case 'quote':
      return (
        <QuoteView
          key={key}
          text={bindPlain(str(p.text), values)}
          author={bindPlain(str(p.author), values)}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'alert':
      return (
        <AlertView
          key={key}
          tone={p.tone as AlertTone | undefined}
          html={bindHtml(str(p.html), values)}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'badge':
      return (
        <BadgeView
          key={key}
          label={bindPlain(str(p.label), values)}
          tone={p.tone as BadgeTone | undefined}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'stat':
      return (
        <StatView
          key={key}
          value={bindPlain(str(p.value), values)}
          label={bindPlain(str(p.label), values)}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'accordion':
      return (
        <AccordionView
          key={key}
          title={bindPlain(str(p.title), values)}
          html={bindHtml(str(p.html), values)}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'list':
      return (
        <ListView
          key={key}
          items={asStringArray(p.items).map((item) => bindPlain(item, values))}
          ordered={p.ordered === true}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'video':
      return (
        <VideoView
          key={key}
          source={(p.source as VideoSource) ?? 'embed'}
          url={str(p.url)}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'icon':
      return (
        <IconView
          key={key}
          name={p.name as IconName}
          size={typeof p.size === 'number' ? p.size : undefined}
          tone={p.tone as IconTone | undefined}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'socialLinks':
      return (
        <SocialLinksView
          key={key}
          items={asSocialLinks(p.items)}
          size={p.size as SocialSize | undefined}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'htmlEmbed':
      return <HtmlEmbedView key={key} html={bindHtml(str(p.html), values)} styleJson={strU(p.styleJson)} />;
    case 'formRef':
      return <FormRefView key={key} formId={str(p.formId)} styleJson={strU(p.styleJson)} />;
    case 'sliderRef':
      return <SliderRefView key={key} sliderId={str(p.sliderId)} styleJson={strU(p.styleJson)} />;
    case 'mapRef':
      return (
        <MapRefView
          key={key}
          lat={Number(p.lat)}
          lng={Number(p.lng)}
          zoom={clampMapZoom(p.zoom)}
          styleJson={strU(p.styleJson)}
        />
      );
    case 'legacyHtml':
      return <LegacyHtmlView key={key} html={bindHtml(str(p.html), values)} />;
    default:
      // Bilinmeyen tip sessizce atılır (BE store-time whitelist zaten reddeder).
      return null;
  }
}

function renderNodes(nodes: JsonNode[], values: Record<string, unknown>, depth: number): ReactNode {
  if (depth > MAX_DEPTH) return null;
  return nodes.map((node, i) => renderNode(node, values, depth, i));
}

/** Ham BE componentJson metninden üst-seviye `content[]` düğümlerini çözer. */
function parseContent(json: string | null | undefined): JsonNode[] {
  if (!json || !json.trim()) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return [];
  }
  if (!raw || typeof raw !== 'object') return [];
  return asNodes((raw as { content?: unknown }).content);
}

export interface ComponentJsonRendererProps {
  /** BE kaynak-hakikat componentJson (`{root:{},content:[]}`). */
  componentJson: string | null | undefined;
  /** Sayfadaki cmp_ blok dış prop değerleri (`{{key}}` binding kaynağı). */
  values?: Record<string, unknown>;
  /** K-25 — güvenli CSS whitelist'iyle per-component wrapper stiline uygulanır. */
  defaultStyleJson?: string | null;
  /** Wrapper sınıfı (sayfa-yapıcıda cmp_ bloğu marker'ı). */
  className?: string;
}

/**
 * componentJson ağacını statik yürüyüşle render eder. Wrapper'a defaultStyle (K-25)
 * uygulanır; dış prop `{{key}}` binding'i güvenli substitüsyonla çözülür.
 */
export function ComponentJsonRenderer({
  componentJson,
  values = {},
  defaultStyleJson,
  className = 'ds-block ds-block--runtime',
}: ComponentJsonRendererProps): ReactElement {
  const nodes = parseContent(componentJson);
  const style: CSSProperties = parseDefaultStyle(defaultStyleJson);
  const hasStyle = Object.keys(style).length > 0;
  return (
    <div className={className} style={hasStyle ? style : undefined}>
      {renderNodes(nodes, values, 0)}
    </div>
  );
}
